<template>
  <div>
    <div
      v-if="!!item.in_progress_at"
      class="flex-center padding-x-8 padding-y-4 border-radius-4 cursor--not-allowed font-size-12 font-weight-500 background-error color-surface"
    >
      {{ $lang.inProgress }}
    </div>
    <div
      v-else
      :class="`flex-center padding-x-8 padding-y-2 border-radius-4 font-size-12 font-weight-500 background-${
        status.backgroundColor
      } color-${status.color} ${
        !allowUserExecutiveRoles(item) ? 'cursor--not-allowed' : ''
      }`"
      @click.stop="handleOpenStepChangeModal"
    >
      {{ status.caption }}
      <span
        v-if="!justView && allowUserExecutiveRoles(item)"
        class="margin-right-8 flex"
      >
        <mdicon name="chevron-down" size="16" />
      </span>
    </div>
    <Modal
      :show="isStepChangeModal"
      :title="$lang.stepChange"
      :closeOnBackdrop="false"
      :hasClose="true"
      :hasFooter="false"
      :cancelButton="false"
      :okButton="false"
      size="sm"
      :contentClass="
        checkScreenDevice(['desktop'])
          ? 'height--max-content min-height--max-content'
          : ''
      "
      @close="handleCloseStepChangeModal"
    >
      <template #body>
        <div class="center-execute-change-status-navigation">
          <div
            v-for="(item, index) in statusItems"
            :key="`status-${index}`"
            class="width-percent-100"
          >
            <div
              class="center-execute-change-status-navigation__box"
              v-if="
                item.show &&
                includes(item.id, [status.id, status.id - 1, status.id + 1])
              "
              :class="{ active: item.id === status.id }"
            >
              <div class="center-execute-change-status-navigation__box-data">
                <span class="box-circle" :class="item.iconClass">
                  <mdicon :name="item.icon" size="24" />
                </span>
                <div
                  class="center-execute-change-status-navigation__data"
                  :class="{
                    'margin-top-16':
                      item.id !== status.id &&
                      checkScreenDevice(['mobile', 'tablet']),
                  }"
                >
                  <div class="box-caption">
                    {{ item.caption }}
                    <span><mdicon name="help-circle-outline" size="16" /></span>
                  </div>
                  <div
                    v-if="item.id === status.id"
                    class="box-buttons"
                    :class="{ 'visibility--hidden': item.id !== status.id }"
                  >
                    <Button
                      v-if="index && item.hasBackButton"
                      variant="outline-primary"
                      :text="$lang.beforeStep"
                      size="sm"
                      rightIcon="arrow-up"
                      wrapperClass="margin-left-4"
                      :loading="prevLoading"
                      :disabled="prevLoading || nextLoading"
                      @click="handleSetStatus(item.id - 1, 'prev')"
                    />
                    <Button
                      v-if="item.hasNextButton"
                      variant="outline-primary"
                      :text="$lang.afterStep"
                      size="sm"
                      leftIcon="arrow-down"
                      wrapperClass="margin-right-4"
                      :loading="nextLoading"
                      :disabled="prevLoading || nextLoading"
                      @click="handleSetStatus(item.id + 1, 'next')"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
import { Modal } from "@/components/re-design";
import { Button } from "@/components/re-design/forms";
import { pathOr, includes } from "ramda";
import { ref, toRefs, inject } from "vue";

import {
  generateError,
  renderEumOptions,
  toastAlert,
  checkScreenDevice,
} from "@/shared/utils";
import { setCenterExecuteState } from "@/modules/kanoon/services";
export default {
  components: { Modal, Button },
  props: {
    item: {
      type: Object,
      default: null,
    },
    justView: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    // generate props data
    const { item, justView } = toRefs(props);

    // injects data
    const checkSharedAccesses = inject("checkSharedAccesses");

    const handleReloadList = () => emit("handleReloadList");

    const statusEnums = renderEumOptions("center_execute_status", true);
    const generateExtraStatusKeys = (id = 1) => {
      const colors = {
        1: {
          backgroundColor: "warning-50",
          color: "warning",
          icon: "pencil-outline",
          show: checkSharedAccesses("STATUS-TYPE-1"),
          hasBackButton: false,
          hasNextButton: checkSharedAccesses("STATUS-TYPE-2"),
        },
        2: {
          backgroundColor: "warning-50",
          color: "warning",
          icon: "send",
          iconClass: "rotate-horizontal-180",
          show: checkSharedAccesses("STATUS-TYPE-2"),
          hasBackButton: checkSharedAccesses("STATUS-TYPE-1"),
          hasNextButton: checkSharedAccesses("STATUS-TYPE-3"),
        },
        3: {
          backgroundColor: "success-50",
          color: "success",
          icon: "timelapse",
          show: checkSharedAccesses("STATUS-TYPE-3"),
          hasBackButton: checkSharedAccesses("STATUS-TYPE-2"),
          hasNextButton: checkSharedAccesses("STATUS-TYPE-4"),
        },
        4: {
          backgroundColor: "warning-50",
          color: "warning",
          icon: "account-edit-outline",
          show: checkSharedAccesses("STATUS-TYPE-4"),
          hasBackButton: checkSharedAccesses("STATUS-TYPE-3"),
          hasNextButton: checkSharedAccesses("STATUS-TYPE-5"),
        },
        5: {
          backgroundColor: "success-50",
          color: "success",
          icon: "check",
          show: checkSharedAccesses("STATUS-TYPE-5"),
          hasBackButton: checkSharedAccesses("STATUS-TYPE-4"),
          hasNextButton: false,
        },
      };
      return pathOr(null, [id], colors);
    };
    const statusItems = statusEnums.map(({ id, caption }) => {
      const colorsObjects = generateExtraStatusKeys(id);
      return {
        id,
        caption,
        show: true,
        ...colorsObjects,
      };
    });

    const allowUserExecutiveRoles = ({ executive_role_id }) => {
      return includes(executive_role_id, [3, null]);
    };

    // handle show modal
    const isStepChangeModal = ref(false);
    const handleOpenStepChangeModal = () => {
      if (justView.value || !allowUserExecutiveRoles(item.value)) return;
      isStepChangeModal.value = true;
    };
    const handleCloseStepChangeModal = () => (isStepChangeModal.value = false);

    const { status: statusId } = item.value;
    const currentStatus = statusItems.find(({ id }) => id === statusId);
    const status = ref(currentStatus);
    const prevLoading = ref(false);
    const nextLoading = ref(false);
    const handleSetStatus = async (statusId, actionType) => {
      if (prevLoading.value || nextLoading.value) return;

      try {
        if (actionType === "prev") {
          prevLoading.value = true;
        }
        if (actionType === "next") {
          nextLoading.value = true;
        }

        const centerExecuteId = pathOr(null, ["id"], item.value);
        const response = await setCenterExecuteState(centerExecuteId, statusId);
        const { message } = response;
        toastAlert(message);
        handleCloseStepChangeModal();
        handleReloadList();
      } catch (error) {
        generateError(error);
      } finally {
        prevLoading.value = false;
        nextLoading.value = false;
      }
    };

    // return data to be used in template
    return {
      item,
      statusItems,
      isStepChangeModal,
      handleOpenStepChangeModal,
      handleCloseStepChangeModal,
      status,
      handleSetStatus,
      pathOr,
      includes,
      prevLoading,
      nextLoading,
      checkScreenDevice,
      allowUserExecutiveRoles,
    };
  },
};
</script>
